import React, { useEffect, useRef, useState } from 'react'
import { ArrowDownUp, CheckCircle2, ChevronDown, Copy, RefreshCw, Search, Settings, Wallet, Waves, X } from 'lucide-react'
import { Connection, VersionedTransaction } from '@solana/web3.js'
import { decodeFunctionResult, encodeFunctionData, erc20Abi, formatUnits, isAddress } from 'viem'
import { get, post, sleep, short } from '../../api/client'
import { useRoseWallet } from '../../wallet/useRoseWallet'
import { ensureVerifiedEvm, ensureVerifiedSolana } from '../../wallet/verify'

const SOL_CHAIN = 1151111081099710
const NATIVE_EVM = '0x0000000000000000000000000000000000000000'
const NATIVE_SOL = '11111111111111111111111111111111'
const WRAPPED_SOL = 'So11111111111111111111111111111111111111112'
const FALLBACK_CHAINS = [
  { id: 1, name: 'Ethereum', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'ETH', decimals: 18 } },
  { id: 10, name: 'Optimism', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'ETH', decimals: 18 } },
  { id: 56, name: 'BNB Chain', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'BNB', decimals: 18 } },
  { id: 137, name: 'Polygon', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'POL', decimals: 18 } },
  { id: 8453, name: 'Base', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'ETH', decimals: 18 } },
  { id: 42161, name: 'Arbitrum', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'ETH', decimals: 18 } },
  { id: 43114, name: 'Avalanche', chainType: 'EVM', nativeToken: { address: NATIVE_EVM, symbol: 'AVAX', decimals: 18 } },
  { id: SOL_CHAIN, name: 'Solana', chainType: 'SVM', nativeToken: { address: NATIVE_SOL, symbol: 'SOL', decimals: 9 } }
]
const RPC = import.meta.env.VITE_SOLANA_RPC_URL || 'https://api.mainnet-beta.solana.com'

const normalizeAmount = value => String(value || '').trim().replace(/^\./, '0.')
const toAtomic = (value, decimals) => {
  const raw = normalizeAmount(value)
  if (!/^\d+(\.\d+)?$/.test(raw)) throw new Error('Enter a valid amount')
  const [whole, frac = ''] = raw.split('.')
  return (BigInt(whole || 0) * 10n ** BigInt(decimals) + BigInt((frac + '0'.repeat(decimals)).slice(0, decimals) || 0)).toString()
}
const fromAtomic = (value, decimals, max = 8) => {
  if (value == null || value === '') return ''
  const n = BigInt(value)
  const base = 10n ** BigInt(decimals)
  const whole = n / base
  const frac = (n % base).toString().padStart(decimals, '0').slice(0, max).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : `${whole}`
}
const nativeToken = (chain, token) => Number(chain) === SOL_CHAIN
  ? [NATIVE_SOL, WRAPPED_SOL].includes(token?.address)
  : String(token?.address || '').toLowerCase() === NATIVE_EVM
const apiTokenAddress = (chain, token) => Number(chain) === SOL_CHAIN && token?.address === WRAPPED_SOL ? NATIVE_SOL : token?.address
const formatUsd = value => Number(value) > 0 ? `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'
const cleanProviderName = value => String(value || '').replace(/LI\.FI/gi, 'Rose bridge').replace(/Jupiter(?: Swap V2)?/gi, 'Rose swap')

function TokenLogo({ token }) {
  const [failed, setFailed] = useState(false)
  const src = token?.logoURI || token?.logoUri
  return src && !failed
    ? <img className="trade-token-logo" src={src} alt="" onError={() => setFailed(true)} />
    : <i className="trade-token-fallback">{token?.symbol?.[0] || '?'}</i>
}

function ChainLogo({ chain }) {
  const [failed, setFailed] = useState(false)
  const src = chain?.logoURI || chain?.logoUri
  return src && !failed
    ? <img className="trade-chain-logo" src={src} alt="" onError={() => setFailed(true)} />
    : <i className="trade-chain-fallback">{chain?.name?.[0] || '?'}</i>
}

function TokenIdentity({ token }) {
  return <span className="token-identity"><TokenLogo token={token} /><span><b>{token?.symbol}</b><small>{token?.name}</small></span>{token?.verified && <CheckCircle2 />}</span>
}

function ChainIdentity({ chain }) {
  return <span className="token-identity"><ChainLogo chain={chain} /><span><b>{chain?.name}</b><small>{chain?.nativeToken?.name || chain?.chainType}</small></span></span>
}

function QuoteRow({ label, value }) {
  return <div className="quote-row"><span>{label}</span><b>{value}</b></div>
}

function ChainModal({ chains, selected, onSelect, onClose }) {
  const [query, setQuery] = useState('')
  const filtered = chains.filter(chain => `${chain.name} ${chain.key || ''} ${chain.nativeToken?.symbol || ''}`.toLowerCase().includes(query.toLowerCase()))
  return <div className="trade-modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <div className="trade-token-modal panel chain-modal">
      <div className="token-modal-head"><div><span>Rose supported networks</span><h3>Select network</h3></div><button onClick={onClose}><X /></button></div>
      <label className="token-search"><Search /><input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Search network" /></label>
      <div className="token-result-list">
        {filtered.map(chain => <button key={chain.id} className={Number(selected) === Number(chain.id) ? 'selected' : ''} onClick={() => onSelect(chain)}>
          <ChainIdentity chain={chain} />
          <span className="token-result-meta"><b>{chain.nativeToken?.symbol}</b><small>{chain.chainType}</small></span>
          {Number(selected) === Number(chain.id) && <CheckCircle2 />}
        </button>)}
      </div>
    </div>
  </div>
}

function TokenModal({ chain, selected, onSelect, onClose }) {
  const [query, setQuery] = useState('')
  const [tokens, setTokens] = useState([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const isSolana = Number(chain) === SOL_CHAIN

  useEffect(() => {
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      setBusy(true)
      setError('')
      try {
        const q = query.trim() ? `&query=${encodeURIComponent(query.trim())}` : ''
        const path = isSolana
          ? `/api/v1/trade/jupiter/tokens?limit=100${q}`
          : `/api/v1/trade/lifi/tokens?chains=${encodeURIComponent(chain)}&limit=100${q}`
        const result = await get(path, { signal: controller.signal })
        setTokens(result.tokens || [])
      } catch (err) {
        if (err.name !== 'AbortError') setError('Rose token index is temporarily unavailable.')
      } finally { if (!controller.signal.aborted) setBusy(false) }
    }, query ? 350 : 0)
    return () => { clearTimeout(timer); controller.abort() }
  }, [query, chain, isSolana])

  return <div className="trade-modal-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
    <div className="trade-token-modal panel">
      <div className="token-modal-head"><div><span>Rose token search</span><h3>Select token</h3></div><button onClick={onClose}><X /></button></div>
      <label className="token-search"><Search /><input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder={isSolana ? 'Search symbol/name or paste mint' : 'Search symbol/name or paste token address'} />{busy && <RefreshCw className="spin" />}</label>
      <div className="token-source"><span>Rose indexed tokens</span><small>{tokens.length} results</small></div>
      {error && <div className="trade-error">{error}</div>}
      <div className="token-result-list">
        {tokens.map(token => <button key={`${token.chainId || chain}:${token.address}`} className={token.address === selected?.address ? 'selected' : ''} onClick={() => onSelect(token)}>
          <TokenIdentity token={token} />
          <span className="token-result-meta"><b>{token.balance ? `${token.balance} ${token.symbol}` : ''}</b><small>{short(token.address)}</small></span>
          {token.address === selected?.address && <CheckCircle2 />}
        </button>)}
        {!busy && !tokens.length && <div className="trade-empty">No matching token found for this network.</div>}
      </div>
    </div>
  </div>
}

function NetworkBox({ label, chain, token, amount, setAmount, balance, output, onChain, onToken, onMax, walletAddress, walletLabel, onWallet }) {
  return <div className={`token-box network-box ${output ? 'output' : ''}`}>
    <div className="network-box-head">
      <span>{label}</span>
      <div className="network-box-actions">
        <button className="network-pill" onClick={onChain}><ChainLogo chain={chain} /><span>{chain?.name || 'Network'}</span><ChevronDown /></button>
        <button className={`network-wallet-pill ${walletAddress ? 'connected' : ''}`} onClick={onWallet}><Wallet /><span>{walletAddress ? short(walletAddress) : `Connect ${walletLabel || 'wallet'}`}</span></button>
      </div>
    </div>
    <div className="token-row">
      <input inputMode="decimal" value={amount || ''} onChange={e => setAmount?.(e.target.value)} readOnly={output} placeholder="0.00" />
      <button onClick={onToken}><TokenLogo token={token} /><span>{token?.symbol || 'Select'}</span><ChevronDown /></button>
    </div>
    <small>{balance ? `Balance: ${balance} ${token?.symbol || ''}` : ''}{onMax && <button className="max-link" onClick={onMax}>MAX</button>}</small>
  </div>
}

const waitReceipt = async (provider, hash) => {
  for (let i = 0; i < 90; i += 1) {
    const receipt = await provider.request({ method: 'eth_getTransactionReceipt', params: [hash] }).catch(() => null)
    if (receipt) {
      if (receipt.status === '0x0') throw new Error('EVM transaction reverted')
      return receipt
    }
    await sleep(1500)
  }
  throw new Error('EVM transaction confirmation timed out')
}

export default function BridgePage() {
  const { solana, evm, solanaProvider, evmProvider, open } = useRoseWallet()
  const [providers, setProviders] = useState(null)
  const [chains, setChains] = useState(FALLBACK_CHAINS)
  const [fromChain, setFromChain] = useState(8453)
  const [toChain, setToChain] = useState(SOL_CHAIN)
  const [fromToken, setFromToken] = useState({ address: NATIVE_EVM, symbol: 'ETH', name: 'Ether', decimals: 18, chainId: 8453 })
  const [toToken, setToToken] = useState({ address: NATIVE_SOL, symbol: 'SOL', name: 'Solana', decimals: 9, chainId: SOL_CHAIN })
  const [amount, setAmount] = useState('')
  const [recipient, setRecipient] = useState('')
  const [slippage, setSlippage] = useState(.005)
  const [balance, setBalance] = useState('0')
  const [preview, setPreview] = useState(null)
  const [record, setRecord] = useState(null)
  const [working, setWorking] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [message, setMessage] = useState('')
  const [chainModal, setChainModal] = useState(null)
  const [tokenModal, setTokenModal] = useState(null)
  const abort = useRef(null)

  const sourceIsSol = Number(fromChain) === SOL_CHAIN
  const destIsSol = Number(toChain) === SOL_CHAIN
  const sourceAddress = sourceIsSol ? solana.address : evm.address
  const destWallet = destIsSol ? solana.address : evm.address
  const sourceChain = chains.find(chain => Number(chain.id) === Number(fromChain)) || FALLBACK_CHAINS.find(chain => Number(chain.id) === Number(fromChain))
  const destChain = chains.find(chain => Number(chain.id) === Number(toChain)) || FALLBACK_CHAINS.find(chain => Number(chain.id) === Number(toChain))
  const sourceWalletLabel = sourceIsSol ? 'Solana' : 'EVM'
  const destWalletLabel = destIsSol ? 'Solana' : 'EVM'

  useEffect(() => {
    Promise.allSettled([get('/api/v1/trade/providers'), get('/api/v1/trade/lifi/chains'), get('/api/v1/trade/defaults')]).then(([providerResult, chainResult, defaultsResult]) => {
      if (providerResult.status === 'fulfilled') setProviders(providerResult.value)
      if (chainResult.status === 'fulfilled' && chainResult.value?.chains?.length) setChains(chainResult.value.chains)
      if (defaultsResult.status === 'fulfilled' && defaultsResult.value?.bridgeSolanaDestination?.address && Number.isInteger(Number(defaultsResult.value.bridgeSolanaDestination.decimals)) && Number(toChain) === SOL_CHAIN) {
        setToToken(defaultsResult.value.bridgeSolanaDestination)
      }
    })
  }, [])

  useEffect(() => {
    const chain = chains.find(value => Number(value.id) === Number(fromChain))
    setFromToken(Number(fromChain) === SOL_CHAIN
      ? { address: NATIVE_SOL, symbol: 'SOL', name: 'Solana', decimals: 9, chainId: SOL_CHAIN }
      : { ...(chain?.nativeToken || {}), address: chain?.nativeToken?.address || NATIVE_EVM, chainId: Number(fromChain) })
    setPreview(null)
  }, [fromChain, chains])

  useEffect(() => {
    const chain = chains.find(value => Number(value.id) === Number(toChain))
    if (Number(toChain) !== SOL_CHAIN) setToToken({ ...(chain?.nativeToken || {}), address: chain?.nativeToken?.address || NATIVE_EVM, chainId: Number(toChain) })
    setRecipient(destWallet || '')
    setPreview(null)
  }, [toChain, destWallet, chains])

  const loadBalance = async () => {
    if (!sourceAddress || !fromToken) { setBalance('0'); return }
    try {
      if (sourceIsSol) {
        const result = await get(`/api/v1/trade/solana/balances?address=${encodeURIComponent(sourceAddress)}`)
        setBalance(result.balances?.[fromToken.address]?.uiAmountString || result.balances?.[WRAPPED_SOL]?.uiAmountString || '0')
        return
      }
      if (!evmProvider?.request) { setBalance('0'); return }
      const current = await evmProvider.request({ method: 'eth_chainId' })
      if (Number.parseInt(current, 16) !== Number(fromChain)) { setBalance('0'); return }
      if (nativeToken(fromChain, fromToken)) {
        const hex = await evmProvider.request({ method: 'eth_getBalance', params: [sourceAddress, 'latest'] })
        setBalance(formatUnits(BigInt(hex), Number(fromToken.decimals || 18)))
      } else {
        const data = encodeFunctionData({ abi: erc20Abi, functionName: 'balanceOf', args: [sourceAddress] })
        const raw = await evmProvider.request({ method: 'eth_call', params: [{ to: fromToken.address, data }, 'latest'] })
        const value = decodeFunctionResult({ abi: erc20Abi, functionName: 'balanceOf', data: raw })
        setBalance(formatUnits(value, Number(fromToken.decimals || 18)))
      }
    } catch { setBalance('0') }
  }
  useEffect(() => { loadBalance() }, [sourceAddress, fromChain, fromToken?.address])

  const build = () => {
    if (!sourceAddress) throw new Error(`Connect the source ${sourceWalletLabel} wallet`)
    if (!recipient || recipient.length < 20) throw new Error('Enter a receiving wallet')
    return {
      from_chain: Number(fromChain),
      to_chain: Number(toChain),
      from_token: apiTokenAddress(fromChain, fromToken),
      to_token: apiTokenAddress(toChain, toToken),
      from_amount: toAtomic(amount, Number(fromToken.decimals)),
      from_address: sourceAddress,
      to_address: recipient,
      slippage: Number(slippage),
      trade_type: Number(fromChain) === Number(toChain) ? 'crosschain-swap' : 'bridge'
    }
  }

  useEffect(() => {
    abort.current?.abort()
    setPreview(null)
    if (!sourceAddress || !fromToken || !toToken || !recipient || recipient.length < 20 || Number(normalizeAmount(amount)) <= 0) return
    const controller = new AbortController()
    abort.current = controller
    const timer = setTimeout(async () => {
      setPreviewing(true)
      try { setPreview(await post('/api/v1/trade/lifi/preview', build(), { signal: controller.signal })); setMessage('') }
      catch (err) { if (err.name !== 'AbortError') { setPreview(null); setMessage(err.message) } }
      finally { if (!controller.signal.aborted) setPreviewing(false) }
    }, 350)
    return () => { clearTimeout(timer); controller.abort() }
  }, [sourceAddress, recipient, amount, fromChain, toChain, fromToken?.address, toToken?.address, slippage])

  const switchEvm = async chainId => {
    if (!evmProvider?.request) throw new Error('EVM wallet provider unavailable')
    await evmProvider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${Number(chainId).toString(16)}` }] })
  }
  const sendSolana = async quote => {
    await ensureVerifiedSolana(solana.address, solanaProvider)
    const data = quote?.quote?.transactionRequest?.data
    if (!data) throw new Error('Bridge route did not return Solana transaction data')
    const tx = VersionedTransaction.deserialize(Uint8Array.from(atob(data), c => c.charCodeAt(0)))
    const connection = new Connection(RPC, 'confirmed')
    if (solanaProvider?.sendTransaction) {
      const value = await solanaProvider.sendTransaction(tx, connection)
      return typeof value === 'string' ? value : value?.signature
    }
    if (solanaProvider?.signTransaction) {
      const signed = await solanaProvider.signTransaction(tx)
      return connection.sendRawTransaction(signed.serialize(), { skipPreflight: false, maxRetries: 5 })
    }
    throw new Error('Connected Solana wallet cannot sign and send this transaction')
  }
  const sendEvm = async quote => {
    await ensureVerifiedEvm(evm.address, evmProvider)
    const route = quote?.quote || {}
    const tx = route.transactionRequest
    const chainId = Number(route.action?.fromChainId || fromChain)
    if (!tx?.to || !tx?.data) throw new Error('Bridge route did not return an executable EVM transaction')
    if (!isAddress(tx.to)) throw new Error('Bridge returned an invalid EVM target')
    if (tx.from && String(tx.from).toLowerCase() !== String(evm.address).toLowerCase()) throw new Error('Transaction sender does not match the verified EVM wallet')
    await switchEvm(chainId)
    const token = route.action?.fromToken
    const amountIn = BigInt(route.action?.fromAmount || 0)
    const spender = route.estimate?.approvalAddress
    if (token && !nativeToken(chainId, token)) {
      if (!isAddress(token.address) || !isAddress(spender)) throw new Error('Bridge returned an invalid approval target')
      const allowanceData = encodeFunctionData({ abi: erc20Abi, functionName: 'allowance', args: [evm.address, spender] })
      const raw = await evmProvider.request({ method: 'eth_call', params: [{ to: token.address, data: allowanceData }, 'latest'] })
      const allowance = decodeFunctionResult({ abi: erc20Abi, functionName: 'allowance', data: raw })
      if (allowance < amountIn) {
        if (allowance > 0n) {
          setMessage('Resetting existing token approval…')
          const reset = await evmProvider.request({ method: 'eth_sendTransaction', params: [{ from: evm.address, to: token.address, data: encodeFunctionData({ abi: erc20Abi, functionName: 'approve', args: [spender, 0n] }) }] })
          await waitReceipt(evmProvider, reset)
        }
        setMessage('Approve the exact bridge amount…')
        const approval = await evmProvider.request({ method: 'eth_sendTransaction', params: [{ from: evm.address, to: token.address, data: encodeFunctionData({ abi: erc20Abi, functionName: 'approve', args: [spender, amountIn] }) }] })
        await waitReceipt(evmProvider, approval)
      }
    }
    setMessage('Confirm the source transaction…')
    const request = { from: evm.address, to: tx.to, data: tx.data, value: tx.value ? `0x${BigInt(tx.value).toString(16)}` : '0x0' }
    if (tx.gasLimit) request.gas = `0x${BigInt(tx.gasLimit).toString(16)}`
    if (tx.maxFeePerGas) {
      request.maxFeePerGas = `0x${BigInt(tx.maxFeePerGas).toString(16)}`
      if (tx.maxPriorityFeePerGas) request.maxPriorityFeePerGas = `0x${BigInt(tx.maxPriorityFeePerGas).toString(16)}`
    } else if (tx.gasPrice) request.gasPrice = `0x${BigInt(tx.gasPrice).toString(16)}`
    return evmProvider.request({ method: 'eth_sendTransaction', params: [request] })
  }

  const execute = async () => {
    setWorking(true)
    setMessage('Preparing the bridge for wallet review…')
    try {
      const quote = await post('/api/v1/trade/lifi/quote', build())
      setPreview(quote)
      const hash = sourceIsSol ? await sendSolana(quote) : await sendEvm(quote)
      if (!hash) throw new Error('Source wallet did not return a transaction signature')
      const next = await post('/api/v1/trade/lifi/submit', { quote_id: quote.id, source_tx_hash: hash })
      setRecord({ ...next, sourceTxHash: hash })
      setMessage(`Bridge submitted · ${short(hash)}`)
      await loadBalance()
    } catch (err) { setMessage(err.message) } finally { setWorking(false) }
  }
  const refresh = async () => {
    if (!record?.id) return
    setWorking(true)
    try { setRecord({ ...record, ...await post(`/api/v1/trade/lifi/${record.id}/refresh`) }) }
    catch (err) { setMessage(err.message) } finally { setWorking(false) }
  }
  useEffect(() => {
    if (!record?.id || ['complete', 'failed', 'partial', 'refunded'].includes(String(record.status).toLowerCase())) return
    const timer = setInterval(() => post(`/api/v1/trade/lifi/${record.id}/refresh`).then(value => setRecord(current => ({ ...current, ...value }))).catch(() => {}), 15000)
    return () => clearInterval(timer)
  }, [record?.id, record?.status])

  const estimate = preview?.quote?.estimate || {}
  const outDecimals = Number(preview?.quote?.action?.toToken?.decimals ?? toToken?.decimals ?? 6)
  const receive = estimate.toAmount ? fromAtomic(estimate.toAmount, outDecimals, 9) : ''
  const routeCosts = [...(estimate.gasCosts || []), ...(estimate.feeCosts || [])].reduce((sum, cost) => sum + (Number(cost.amountUSD) || 0), 0)
  const routeSteps = preview?.quote?.includedSteps || []
  const reverse = () => {
    const oldChain = fromChain
    const oldToken = fromToken
    setFromChain(toChain)
    setToChain(oldChain)
    setFromToken(toToken)
    setToToken(oldToken)
    setAmount('')
    setPreview(null)
  }
  const walletTarget = (address, namespace) => open(address ? { view: 'Account' } : { view: 'Connect', namespace })
  const execution = Boolean(providers?.providers?.lifi?.execution)

  return <div className="rose-trade-page compact-trade-page">
    <div className="trade-compact-head">
      <div><div className="eyebrow"><Wallet size={14} />Rose Trade</div><h1>Bridge assets</h1><p>Move assets between Solana and supported EVM networks with the correct wallet for each source chain.</p></div>
      <details className="trade-settings-menu">
        <summary title="Bridge settings"><Settings /></summary>
        <div className="panel"><b>Bridge settings</b><label><span>Slippage</span><div>{[.001, .005, .01].map(value => <button key={value} className={slippage === value ? 'active' : ''} onClick={() => setSlippage(value)}>{value * 100}%</button>)}</div></label><button className="secondary full" onClick={loadBalance}><RefreshCw />Refresh source balance</button></div>
      </details>
    </div>

    <div className="compact-trade-shell bridge-shell">
      <section className="swap-card panel compact-swap-card compact-bridge-card">
        <NetworkBox label="From" chain={sourceChain} token={fromToken} amount={amount} setAmount={setAmount} balance={balance} onChain={() => setChainModal('from')} onToken={() => setTokenModal('from')} onMax={() => setAmount(balance)} walletAddress={sourceAddress} walletLabel={sourceWalletLabel} onWallet={() => walletTarget(sourceAddress, sourceIsSol ? 'solana' : 'eip155')} />

        <button className="flip-btn" onClick={reverse} aria-label="Reverse bridge direction"><ArrowDownUp /></button>

        <NetworkBox label="To" chain={destChain} token={toToken} amount={receive} output onChain={() => setChainModal('to')} onToken={() => setTokenModal('to')} walletAddress={destWallet} walletLabel={destWalletLabel} onWallet={() => walletTarget(destWallet, destIsSol ? 'solana' : 'eip155')} />

        <details className="trade-disclosure inline recipient-disclosure">
          <summary><span><b>Receiving wallet</b><small>{short(recipient)} · change only when needed</small></span><ChevronDown /></summary>
          <label className="field bridge-address"><span>{destIsSol ? 'Solana address' : 'EVM address'}</span><div className="input-wrap"><input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder={destIsSol ? 'Paste Solana address' : 'Paste EVM address'} /><button type="button" onClick={() => navigator.clipboard?.writeText(recipient)}><Copy /></button></div></label>
        </details>

        <div className="bridge-quote-strip">
          <span>{previewing ? <><RefreshCw className="spin" />Updating route</> : preview ? <><CheckCircle2 />Route ready</> : <><Waves />Enter an amount</>}</span>
          {preview && <><b>{receive} {toToken?.symbol}</b><small>{estimate.executionDuration ? `~${Math.ceil(estimate.executionDuration / 60)} min` : ''}{routeCosts ? ` · ${formatUsd(routeCosts)} costs` : ''}</small></>}
        </div>

        {execution
          ? <button className="primary full trade-submit" disabled={working || previewing || !preview || !sourceAddress} onClick={sourceAddress ? execute : () => walletTarget(sourceAddress, sourceIsSol ? 'solana' : 'eip155')}>{working ? <RefreshCw className="spin" /> : <Waves />}{sourceAddress ? 'Review and bridge' : `Connect ${sourceWalletLabel} wallet`}</button>
          : <button className="primary full trade-submit" disabled><Wallet />Bridge temporarily unavailable</button>}

        {message && <div className="trade-message">{message}</div>}

        <details className="trade-disclosure inline">
          <summary><span><b>Advanced details</b><small>Minimum, fees and settlement steps</small></span><ChevronDown /></summary>
          <div className="quote-summary compact">
            <QuoteRow label="Minimum received" value={estimate.toAmountMin ? `${fromAtomic(estimate.toAmountMin, outDecimals, 9)} ${toToken?.symbol}` : '—'} />
            <QuoteRow label="Estimated time" value={estimate.executionDuration ? `~${Math.ceil(estimate.executionDuration / 60)} min` : '—'} />
            <QuoteRow label="Bridge and gas costs" value={routeCosts ? formatUsd(routeCosts) : '—'} />
            <QuoteRow label="Source wallet" value={`${sourceWalletLabel} · ${short(sourceAddress || '')}`} />
            {routeSteps.length > 0 && <div className="compact-route-steps">
              {routeSteps.map((step, index) => <div className="rose-route-step" key={step.id || index}>
                <i>{index + 1}</i>
                <span><b>{cleanProviderName(step.toolDetails?.name || step.tool || step.type || 'Route step')}</b><small>{step.action?.fromToken?.symbol || ''} → {step.action?.toToken?.symbol || ''}</small></span>
              </div>)}
            </div>}
          </div>
        </details>

        {record && <div className="compact-tracking">
          <span><CheckCircle2 /><b>{cleanProviderName(record.providerStatus || record.status || 'Submitted')}</b></span>
          <code>{short(record.sourceTxHash || record.source_tx_hash || '')}</code>
          <button className="secondary" disabled={working} onClick={refresh}><RefreshCw />Refresh</button>
        </div>}
      </section>
    </div>

    {chainModal && <ChainModal chains={chains} selected={chainModal === 'from' ? fromChain : toChain} onSelect={chain => { chainModal === 'from' ? setFromChain(Number(chain.id)) : setToChain(Number(chain.id)); setChainModal(null) }} onClose={() => setChainModal(null)} />}
    {tokenModal && <TokenModal chain={tokenModal === 'from' ? fromChain : toChain} selected={tokenModal === 'from' ? fromToken : toToken} onSelect={token => { tokenModal === 'from' ? setFromToken(token) : setToToken(token); setTokenModal(null); setPreview(null) }} onClose={() => setTokenModal(null)} />}
  </div>
}
